import type { VNodeChild } from 'vue';
import type { BlockDefinition } from '#layers/block-engine/types/block';
import type {
	FormSchemaKind as FormSchemaKindType,
	FormSchemaSection,
	FormSchemaSections,
	FormSchemaSectionsItem,
	FormSchemaStructuralEntry,
} from '#layers/block-engine/types/form-schema';
import type {
	FormSchemaChrome,
	FormSchemaChromeSectionItem,
	FormSchemaSectionsChrome,
	FormSchemaSectionsSingleSlot,
	FormSchemaSectionsSingleSlotPayload,
} from '#layers/block-engine/utils/block/api/config';
import { FormSchemaKind } from '#layers/block-engine/types/form-schema';
import { getBlockConfig } from '#layers/block-engine/utils/block/api/config';
import { isBlockDefinition } from '#layers/block-engine/utils/block/editor';
import { resolveBuiltinBlock } from '#layers/block-engine/utils/block/resolve';

export type FormSchemaChromeBlock = FormSchemaSection['block'] | FormSchemaSections['block'];

export interface NormalizedFormSchemaSectionsChrome {
	/** Converts normalized section items into props for the chrome block. */
	props: <TResult extends object>(payload: { sections: FormSchemaChromeSectionItem<TResult>[] }) => Record<string, unknown>;
	/** Resolve the chrome-specific named slot for a normalized section item. */
	slot?: <TResult extends object>(item: FormSchemaChromeSectionItem<TResult>) => string | undefined;
	/** Shared slot renderer used when named slots are not configured. */
	singleSlot: NormalizedFormSchemaSectionsSingleSlot;
}

export interface NormalizedFormSchemaSectionsSingleSlot {
	/** Slot name exposed by the chrome block for section content. */
	name: string;
	/** Render section content from the shared slot scope. */
	render: <TResult extends object>(payload: FormSchemaSectionsSingleSlotPayload<TResult>) => VNodeChild;
}

export interface NormalizeFormSchemaSectionsOptions<TResult extends object> {
	/** Sections entry being normalized. */
	entry: FormSchemaSections<TResult>;
	/** Index of this structural entry in its rendered sibling list. */
	entryIndex: number;
	/** Optional pre-filtered sections. */
	sections?: readonly FormSchemaSectionsItem<TResult>[];
}

/**
 * Return a structural entry's explicit chrome block.
 */
export function getFormSchemaEntryBlock<TEntry extends FormSchemaStructuralEntry>(entry: TEntry): TEntry['block'] {
	if (!entry.block) {
		throw new Error(`FormSchema ${entry.kind} entries require a block.`);
	}

	return entry.block;
}

/**
 * Read a block's FormSchema chrome compatibility declaration.
 */
export function getFormSchemaChrome(block: FormSchemaChromeBlock): FormSchemaChrome | undefined {
	const definition = resolveFormSchemaChromeBlock(block);

	return definition ? getBlockConfig(definition).formSchema?.chrome : undefined;
}

/**
 * Resolve a chrome block reference into a synchronously readable block definition.
 */
function resolveFormSchemaChromeBlock(block: FormSchemaChromeBlock): BlockDefinition | undefined {
	if (typeof block === 'string') {
		return resolveBuiltinBlock(block);
	}

	if (isBlockDefinition(block)) {
		return block;
	}
}

/**
 * Assert that a block declares chrome compatible with the requested kind.
 */
export function requireFormSchemaChrome(kind: FormSchemaKindType, block: FormSchemaChromeBlock): FormSchemaChrome {
	const definition = resolveFormSchemaChromeBlock(block);

	// Chrome must be readable before rendering; separate the resolution failure
	// from a resolved block that simply lacks a chrome declaration.
	if (!definition) {
		throw new Error(`Block "${getFormSchemaChromeBlockId(block)}" cannot be resolved for FormSchema ${kind} chrome; use a built-in block id or a BlockDefinition.`);
	}

	const chrome = getBlockConfig(definition).formSchema?.chrome;

	if (kind === FormSchemaKind.Section && chrome && 'section' in chrome) {
		return chrome;
	}

	if (kind === FormSchemaKind.Sections && chrome && 'sections' in chrome) {
		return chrome;
	}

	throw new Error(`Block "${getFormSchemaChromeBlockId(block)}" does not declare FormSchema ${kind} chrome.`);
}

/**
 * Apply default sections chrome settings.
 */
export function normalizeFormSchemaSectionsChrome(chrome?: FormSchemaSectionsChrome): NormalizedFormSchemaSectionsChrome {
	return {
		props: chrome?.props ?? getDefaultFormSchemaSectionsProps,
		...(chrome?.slot ? { slot: chrome.slot } : {}),
		singleSlot: normalizeFormSchemaSectionsSingleSlot(chrome && 'singleSlot' in chrome ? chrome.singleSlot : undefined),
	};
}

/**
 * Normalize section props into chrome items and generated slot names.
 */
export function normalizeFormSchemaSections<TResult extends object>(
	options: NormalizeFormSchemaSectionsOptions<TResult>,
): FormSchemaChromeSectionItem<TResult>[] {
	const sections = options.sections ?? options.entry.sections;

	return sections.map((section, sectionIndex) => {
		// Chrome callbacks receive a copy so they cannot mutate reusable schema props.
		const props = { ...section.props };
		const existingSlot = props.slot;
		// Filtered sections retain source positions because slot names also serve as Vue keys.
		const sourceIndex = options.entry.sections.indexOf(section);
		const stableSectionIndex = sourceIndex === -1 ? sectionIndex : sourceIndex;
		const slotName = typeof existingSlot === 'string' && existingSlot.length > 0
			? existingSlot
			: createFormSchemaSectionSlotName(options.entryIndex, stableSectionIndex);
		const item: FormSchemaChromeSectionItem<TResult> = {
			section,
			props,
			index: sectionIndex,
			slotName,
		};

		return item;
	});
}

/**
 * Convert normalized section items into the default chrome block props.
 */
function getDefaultFormSchemaSectionsProps<TResult extends object>(payload: {
	sections: FormSchemaChromeSectionItem<TResult>[];
}): Record<string, unknown> {
	return {
		items: payload.sections.map((item) => item.props),
	};
}

/**
 * Return a stable label for diagnostics.
 */
function getFormSchemaChromeBlockId(block: FormSchemaChromeBlock): string {
	if (typeof block === 'string') {
		return block;
	}

	return block.id;
}

/**
 * Build a stable internal slot name for generated section content.
 */
function createFormSchemaSectionSlotName(entryIndex: number, sectionIndex: number): string {
	return `form-schema-section-${entryIndex}-${sectionIndex}`;
}

/**
 * Apply default single-slot chrome settings.
 */
function normalizeFormSchemaSectionsSingleSlot(singleSlot?: FormSchemaSectionsSingleSlot): NormalizedFormSchemaSectionsSingleSlot {
	return {
		name: singleSlot?.name ?? 'content',
		render: singleSlot?.render ?? getDefaultFormSchemaSectionsSingleSlotRender,
	};
}

/**
 * Render every visible section through the default shared slot.
 */
function getDefaultFormSchemaSectionsSingleSlotRender<TResult extends object>(
	payload: FormSchemaSectionsSingleSlotPayload<TResult>,
): VNodeChild {
	return payload.renderSections(payload.sections);
}
